import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Lock, DollarSign, TrendingUp, Users, FileText, 
  RefreshCw, Download, AlertCircle, CheckCircle, Clock
} from "lucide-react";
import { API, LOGO_URL } from "../common/constants";

const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Check if already authenticated (session storage)
  useEffect(() => {
    const savedAuth = sessionStorage.getItem("admin_authenticated");
    if (savedAuth === "true") {
      setAuthenticated(true);
      loadData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setAuthenticated(true);
        sessionStorage.setItem("admin_authenticated", "true");
        sessionStorage.setItem("admin_token", response.data.token);
        loadData();
      }
    } catch (err) {
      setError("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setRefreshing(true);
    const token = sessionStorage.getItem("admin_token");
    
    try {
      const [statsRes, txnsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/transactions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setTransactions(txnsRes.data);
    } catch (err) {
      console.error("Failed to load data:", err);
      if (err.response?.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem("admin_authenticated");
        sessionStorage.removeItem("admin_token");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    sessionStorage.removeItem("admin_authenticated");
    sessionStorage.removeItem("admin_token");
    setStats(null);
    setTransactions([]);
  };

  const exportCSV = () => {
    if (!transactions.length) return;
    
    const headers = ["Date", "Customer", "Email", "Package", "Revenue", "AI Cost", "Net Profit", "Status"];
    const rows = transactions.map(t => [
      new Date(t.created_at).toLocaleDateString(),
      t.user_name || "N/A",
      t.user_email || "N/A",
      t.package_type,
      `$${t.amount?.toFixed(2) || "0.00"}`,
      `$${t.ai_cost?.toFixed(4) || "0.00"}`,
      `$${((t.amount || 0) - (t.ai_cost || 0)).toFixed(2)}`,
      t.payment_status
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aiquires_transactions_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // Login Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-teal-400" />
            </div>
            <CardTitle>Admin Dashboard</CardTitle>
            <CardDescription>Enter your admin password to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  autoFocus
                />
              </div>
              {error && (
                <div className="text-sm text-red-400 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Verifying..." : "Login"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-muted-foreground hover:text-teal-400">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="AiQ Phoenix Tech" className="h-10 w-auto rounded-md" />
            <Badge variant="outline" className="border-amber-500/50 text-amber-400">
              Admin Dashboard
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={loadData} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container-main py-8 space-y-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                    <p className="text-2xl font-mono font-bold text-green-400">
                      ${stats.total_revenue?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-400/20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">AI Costs</p>
                    <p className="text-2xl font-mono font-bold text-red-400">
                      ${stats.total_ai_cost?.toFixed(4) || "0.00"}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-red-400/20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Net Profit</p>
                    <p className="text-2xl font-mono font-bold text-teal-400">
                      ${stats.net_profit?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-teal-400/20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Paid Customers</p>
                    <p className="text-2xl font-mono font-bold">
                      {stats.total_customers || 0}
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-muted-foreground/20" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Margin Info */}
        {stats && stats.total_revenue > 0 && (
          <Card className="border-teal-500/30">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold mb-1">Profit Margin</h3>
                  <p className="text-sm text-muted-foreground">
                    AI costs are {((stats.total_ai_cost / stats.total_revenue) * 100).toFixed(2)}% of revenue
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-mono font-bold text-teal-400">
                    {(((stats.total_revenue - stats.total_ai_cost) / stats.total_revenue) * 100).toFixed(1)}%
                  </p>
                  <p className="text-xs text-muted-foreground">margin</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription>All paid orders with cost breakdown</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportCSV} disabled={!transactions.length}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No transactions yet</p>
                <p className="text-sm">Paid orders will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2">Date</th>
                      <th className="text-left py-3 px-2">Customer</th>
                      <th className="text-left py-3 px-2">Package</th>
                      <th className="text-right py-3 px-2">Revenue</th>
                      <th className="text-right py-3 px-2">AI Cost</th>
                      <th className="text-right py-3 px-2">Net</th>
                      <th className="text-center py-3 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn, i) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-surface">
                        <td className="py-3 px-2">
                          {new Date(txn.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium">{txn.user_name || "N/A"}</p>
                            <p className="text-xs text-muted-foreground">{txn.user_email || ""}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={
                            txn.package_type === "career_profiler" ? "border-amber-500/50 text-amber-400" :
                            txn.package_type === "premium" ? "border-teal-500/50 text-teal-400" :
                            "border-gray-500/50"
                          }>
                            {txn.package_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-green-400">
                          ${txn.amount?.toFixed(2) || "0.00"}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-red-400">
                          ${txn.ai_cost?.toFixed(4) || "0.00"}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-teal-400">
                          ${((txn.amount || 0) - (txn.ai_cost || 0)).toFixed(2)}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {txn.payment_status === "paid" ? (
                            <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-400 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Waitlist Stats */}
        {stats && (stats.phoenix_waitlist > 0 || stats.scribe_waitlist > 0) && (
          <Card>
            <CardHeader>
              <CardTitle>Waitlist Signups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-amber-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Phoenix IT Hub</p>
                  <p className="text-2xl font-mono font-bold text-amber-400">{stats.phoenix_waitlist || 0}</p>
                </div>
                <div className="p-4 bg-purple-500/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Scribe Flow Studio</p>
                  <p className="text-2xl font-mono font-bold text-purple-400">{stats.scribe_waitlist || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
